import type * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DocumentStatusBadge,
  statusIconTone,
} from "@/features/invoices/DocumentStatusBadge";
import { draftName } from "@/features/drafts/DraftNameBadge";
import {
  DRAFT_KIND_ICON,
  DraftStatusBadge,
  draftIconTone,
  draftResumeHref,
} from "@/features/drafts/draft-kind";
import { useDrafts } from "@/features/drafts/use-document-draft";
import {
  DOCUMENT_AMOUNT_STYLE,
  DOCUMENT_KIND_HREF,
  DOCUMENT_KIND_ICON,
} from "@/features/documents/document-kind";
import { useAuth } from "@/features/auth/auth-context";
import { istDayOfInstant, useFormatDay } from "@/lib/dates";
import type { DashboardDocumentCard } from "@/lib/endpoints";
import { formatRupees } from "@/lib/money";
import { routes } from "@/routes/paths";

/**
 * §11.2's third block — "the last five documents, each with a one-tap re-share".
 *
 * ONE LIST, EVERY KIND. Bills, quotations and delivery challans interleave,
 * because the owner thinks in "what did I make recently", not in document types.
 * The `kind` field decides the icon, the destination and how the amount is
 * presented — through `Record<DocumentKind, …>` maps in
 * `features/documents/document-kind.tsx`, never a ternary here. This list showed
 * only bills and quotations until 2026-08-16, on a card whose DRAFTS half
 * already showed challans; the owner found it from a screenshot.
 *
 * A CHALLAN'S AMOUNT IS NOT MONEY OWED and is not printed as if it were — it is
 * Rule 55(1)'s declared value of the goods that travelled (§17.7). See
 * `DOCUMENT_AMOUNT_STYLE`, which holds that judgement so this component does not
 * re-make it. Nothing else on this screen counts a challan: the month figure,
 * the chart and "Last bill" are invoices only, deliberately.
 *
 * AMOUNTS COME PRE-RESOLVED. The server already applied §9.3 — a TAX_EXTRA
 * quotation reports its PRE-TAX figure here, because that is what its PDF prints
 * and what the customer was told. This component must never re-derive that: it
 * has been got wrong five times in this codebase, always by a screen deciding
 * for itself which of two totals to show.
 *
 * `tabular-nums` on every figure so a column of amounts aligns digit for digit.
 */
export function RecentDocuments({
  documents,
  loading,
}: {
  documents: readonly DashboardDocumentCard[];
  loading: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const formatDay = useFormatDay();
  const { can } = useAuth();

  /*
   * UNFINISHED DOCUMENTS LEAD THE RECENTS (owner's ask, 2026-08-15). "What did
   * I make recently" includes the one he is halfway through — and that one is
   * the most actionable row on the whole dashboard, because it is waiting on
   * him rather than on anyone else. Fetched separately from the dashboard
   * payload: drafts are their own table and the recents list stays a
   * server-composed view of ISSUED documents.
   *
   * BOTH KINDS, TWO REQUESTS, ONE PER MOUNT. The API has no combined endpoint
   * and deliberately so: each mount's capability check is static (§2.1), which
   * it could not be if one endpoint chose its guard from a `?kind=`. So each
   * list is gated on its own capability here too — a viewer who cannot start a
   * quotation has no use for a quotation draft's "Continue".
   *
   * EVERY ROW SAYS WHICH KIND IT IS — through the icon and the `INV`/`QUOT`/
   * `CHAL` token inside its own name. The ids come from ONE sequence shared by
   * every kind, so `DRAFT/7` alone would send him into the bill editor for the
   * quotation he tapped.
   *
   * IT USED TO SAY IT A THIRD TIME, IN WORDS ("Unfinished bill"), and that was
   * dropped on 2026-08-16 when these rows were reshaped to match the document
   * rows below them. The document rows have never named their kind in words —
   * `QOT/545/26` against `INV/26-27/72` leans on exactly the same token — and
   * the sentence was the main thing making a draft row look like a different
   * species. What the words were really protecting was the Marathi reader, since
   * those tokens are untranslated by design; that job now belongs to the
   * translated status badge, which says more than the old sentence did.
   */
  const canBill = can("invoice.create");
  const canQuote = can("quotation.create");
  const canChallan = can("challan.create");
  const invoiceDrafts = useDrafts("INVOICE", canBill);
  const quotationDrafts = useDrafts("QUOTATION", canQuote);
  /*
   * §17.7, added 2026-08-15. A challan draft is saved by the same table and the
   * same router as the other two, so it existed the moment challans shipped —
   * and until this line it appeared NOWHERE the owner would look, which is the
   * exact failure `DraftsStrip` was created to fix for quotations. Gated on its
   * own capability like the other two: someone who cannot raise a challan has no
   * use for a challan draft's "Continue".
   */
  const challanDrafts = useDrafts("CHALLAN", canChallan);
  const draftRows = [
    ...(canBill ? (invoiceDrafts.data ?? []) : []),
    ...(canQuote ? (quotationDrafts.data ?? []) : []),
    ...(canChallan ? (challanDrafts.data ?? []) : []),
  ]
    /*
     * Interleaved by when they were last touched, not grouped by kind: the two
     * lists arrive already sorted that way, and concatenating them would bury a
     * quotation he typed a minute ago under bills from last week. ISO-8601 in
     * UTC sorts correctly as text — every `updatedAt` off this API is that
     * shape, so there is nothing to parse and no timezone to get wrong.
     */
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return (
    <div className="px-4 lg:px-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("home.recent")}</CardTitle>
          <CardDescription>{t("home.recentHelp")}</CardDescription>
          <CardAction>
            {/* `nativeButton={false}` — this is an `<a>`; see InvoiceListScreen. */}
            <Button
              variant="outline"
              size="sm"
              nativeButton={false}
              render={<Link to={routes.invoices} />}
            >
              {t("home.allBills")}
            </Button>
          </CardAction>
        </CardHeader>

        <div className="border-t">
          {loading ? (
            <div className="flex flex-col">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 border-b p-4 last:border-b-0"
                >
                  <Skeleton className="size-8 rounded-md" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : documents.length === 0 && draftRows.length === 0 ? (
            /* An empty book, said plainly. §11.2's empty state — not a zero,
             * which would read as a broken counter on a new install. */
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t("home.noRecent")}
            </p>
          ) : (
            <ul className="flex flex-col">
              {draftRows.map((draft) => (
                <li
                  key={`draft-${draft.kind}-${String(draft.id)}`}
                  className="border-b last:border-b-0"
                >
                  <Link
                    /* THE RIGHT EDITOR. One saved form opens in one screen; a
                     * quotation draft carries a subject, a validity and §9.3's
                     * tax-display mode, none of which the bill editor has a
                     * field for — it would silently drop them on the next
                     * autosave.
                     *
                     * KEYED BY KIND, NOT A TERNARY. This read
                     * `kind === "INVOICE" ? invoiceNew : quotationNew`, which is
                     * right for two kinds and silently wrong for the third: a
                     * CHALLAN draft took the else-branch and opened the
                     * QUOTATION editor. See features/drafts/draft-kind.tsx. */
                    to={draftResumeHref(draft.kind, draft.id)}
                    className="hover:bg-accent/50 flex min-h-14 items-center gap-3 p-4 transition-colors"
                  >
                    {/*
                     * THE TILE WEARS THE BADGE'S COLOUR, here as on every row.
                     * Amber while it is unfinished, neutral once it is READY.
                     * Every draft used to be amber, which put an amber tile
                     * beside a grey "Ready to use" badge: the glyph saying "this
                     * needs you" while the words said "settled".
                     *
                     * The two tones come from the SAME `ICON_TONE` map the
                     * document rows below use, so a draft's amber and an unsent
                     * quotation's amber cannot drift apart.
                     *
                     * THE GLYPH STILL CARRIES THE KIND, and the colour never
                     * does — a receipt is a bill, a page is a quotation, a truck
                     * is a challan, whatever tone the tile is wearing.
                     */}
                    <span
                      className={cn(
                        "flex size-8 shrink-0 items-center justify-center rounded-md",
                        draftIconTone(draft.status),
                      )}
                    >
                      {/* The kind's own icon — the same one the sidebar and the
                       * register use. Keyed by kind so CHALLAN cannot fall
                       * through to the quotation's, which is what a ternary
                       * did here. See features/drafts/draft-kind.tsx. */}
                      {DRAFT_KIND_ICON[draft.kind]}
                    </span>

                    {/*
                     * LAID OUT EXACTLY LIKE THE DOCUMENT ROW BELOW IT — number
                     * and status on the first line, buyer and date on the
                     * second. This row used to be shaped differently: the name
                     * in a coloured pill, the buyer promoted up beside it, and
                     * the kind spelled out in words on the second line. Three
                     * differences from its neighbours, none of them carrying
                     * information the neighbours did not also have, on the one
                     * screen where drafts and documents are read together.
                     */}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex flex-wrap items-center gap-2">
                        {/*
                         * PLAIN, in the same style the document number below
                         * wears. The NAME still leads with its state —
                         * `READY/INV/12` — because that is what makes it
                         * sayable aloud and impossible to mistake for a
                         * document number (§6: a draft has none until saved).
                         */}
                        <span className="font-medium tabular-nums">
                          {draftName(draft.kind, draft.id, draft.status)}
                        </span>
                        {/*
                         * The state in WORDS, and the reason the kind no longer
                         * needs spelling out. `INV`/`QUOT`/`CHAL` inside the
                         * name are untranslated code tokens (§11.4), so the
                         * words used to be the only thing a Marathi reader
                         * could read — but that was before this badge existed,
                         * and the document rows have never named their kind in
                         * words either. Kind now comes from the icon and the
                         * token, exactly as it does one row down.
                         */}
                        <DraftStatusBadge
                          status={draft.status}
                          className="text-xs"
                        />
                      </span>
                      <span className="truncate text-sm text-muted-foreground">
                        {draft.customerName === ""
                          ? t("lbl.customerUnknown")
                          : draft.customerName}{" "}
                        ·{" "}
                        {/*
                         * LAST TOUCHED, not started — this list is interleaved
                         * by `updatedAt` (see the sort above), so a start date
                         * here would run backwards against the order of the
                         * rows and read as a sorting bug. The register's drafts
                         * table is where both dates are shown side by side.
                         *
                         * THE IST DAY OF THE INSTANT. `updatedAt.slice(0, 10)`
                         * takes the UTC day and prints it as an IST one, so
                         * anything touched between 00:00 and 05:30 IST reported
                         * yesterday — and the owner works late.
                         */}
                        {formatDay(istDayOfInstant(draft.updatedAt))}
                      </span>
                    </span>

                    <span className="text-muted-foreground shrink-0 text-sm">
                      {t("draft.resume")}
                    </span>
                  </Link>
                </li>
              ))}
              {documents.map((doc) => (
                <li
                  key={`${doc.kind}-${doc.id}`}
                  className="border-b last:border-b-0"
                >
                  <Link
                    /* KEYED BY KIND, NOT A TERNARY — the same lesson as the
                     * draft rows above, which is where it was learned. This
                     * read `doc.kind === "invoice" ? routes.invoice :
                     * routes.quotation`, so the first challan card the API
                     * sent would have opened the QUOTATION of that id, or a
                     * 404. See features/documents/document-kind.tsx. */
                    to={DOCUMENT_KIND_HREF[doc.kind](doc.id)}
                    className="hover:bg-accent/50 flex min-h-14 items-center gap-3 p-4 transition-colors"
                  >
                    {/*
                     * THE TILE WEARS THE BADGE'S COLOUR (owner's ask,
                     * 2026-08-16): green for issued, red for cancelled, amber
                     * while it waits, grey once it is spent. The tile and the
                     * badge on one row are always the same colour because both
                     * resolve the status through the shared `TONE` table rather
                     * than each picking for itself.
                     *
                     * COLOUR NEVER CARRIES THE KIND — the glyph does, and it has
                     * to: green means "issued" on a bill and "made into a bill"
                     * on a quotation, so reading the kind off the colour would
                     * be reading it off nothing.
                     */}
                    <span
                      className={cn(
                        "flex size-8 shrink-0 items-center justify-center rounded-md",
                        statusIconTone(doc.status, doc.kind),
                      )}
                    >
                      {/* Keyed by kind, for the reason the destination above
                       * is: the glyph is the ONLY thing on the row carrying
                       * what sort of document this is, so a challan wearing a
                       * quotation's page is a row that lies twice. */}
                      {DOCUMENT_KIND_ICON[doc.kind]}
                    </span>

                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium tabular-nums">
                          {doc.number}
                        </span>
                        {/*
                         * WAS `{doc.status}` — the raw Prisma enum, so this row
                         * read "ISSUED" and "PARTIALLY_CONVERTED" to an owner
                         * who does not read English comfortably (§11.4). The
                         * `status.*` keys already existed and nothing used them.
                         * Status is never colour alone (§11.1); the badge
                         * carries the word.
                         */}
                        <DocumentStatusBadge
                          status={doc.status}
                          kind={doc.kind}
                          className="text-xs"
                        />
                      </span>
                      <span className="truncate text-sm text-muted-foreground">
                        {/* `doc.date` is `YYYY-MM-DD` off the wire. Printed raw
                         * it is a database value, not a date a person reads. */}
                        {doc.customerName ?? t("lbl.customerUnknown")} ·{" "}
                        {formatDay(doc.date)}
                      </span>
                    </span>

                    {/*
                     * THE AMOUNT, AND WHETHER IT IS MONEY.
                     *
                     * A bill's total and a quotation's price are money and are
                     * printed as such. A CHALLAN'S IS NOT: Rule 55(1) requires a
                     * taxable value on a delivery challan, so the figure is real
                     * — but it is what the GOODS were worth, not what anyone
                     * owes (§17.7). It therefore prints in muted type under a
                     * caption that says so, in the same words the challan
                     * register and the challan itself already use.
                     *
                     * The choice lives in `DOCUMENT_AMOUNT_STYLE`, keyed by
                     * kind, so a fourth document type has to answer the question
                     * rather than inherit "this is money" by default. A credit
                     * note (§17.3) is money going the OTHER way, which is
                     * exactly the sort of thing that must not be decided here by
                     * an else-branch.
                     */}
                    <AmountCell doc={doc} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

/**
 * One row's figure, presented as what that kind of document's figure IS.
 *
 * `formatRupees` and nothing else — Indian digit grouping, `₹1,45,600.00`, and
 * never `toLocaleString` (CLAUDE.md). `tabular-nums` keeps the column aligned
 * digit for digit whether or not it carries a caption.
 */
function AmountCell({
  doc,
}: {
  doc: DashboardDocumentCard;
}): React.JSX.Element {
  const { t } = useTranslation();
  const style = DOCUMENT_AMOUNT_STYLE[doc.kind];

  return (
    <span className="flex shrink-0 flex-col items-end">
      <span className={cn("tabular-nums", style.className)}>
        {formatRupees(doc.amountPaise)}
      </span>
      {style.captionKey === null ? null : (
        <span className="text-muted-foreground text-xs">
          {t(style.captionKey)}
        </span>
      )}
    </span>
  );
}
